'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { pick } from 'lodash';
import { useTranslations, useLocale } from 'next-intl';
import Amount from '../Amount';
import Comment from '../Comment';
import { composeSelectedFontObject, getClusterStyles, getCommentCssVariables, getDataCssVariables } from '../helpers';
import ScreenName from '../ScreenName/ScreenName';
import StreambotWrapper from '../StreambotWrapper';
import { StreambotComponentProps } from '../types';
import WidgetHeadline from '../WidgetHeadline';
import getDefaultValues from './getDefaultValues';
import type { LastDonationWidgetSettings } from './lastDonationWidgetSettingsSchema';
import { useApiV4WithDemoData } from '@/api';
import useGoogleFonts from '@/fonts/useGoogleFonts';
import { Maybe } from '@/types';
type Author = {
  name: Maybe<string>;
};
const defaultDemoData = [{
  id: 1,
  donated_amount_in_cents: 25000,
  author: {
    name: 'Maxi H.'
  },
  message: 'Yeah weiter so!'
}] satisfies LastDonationData[];
export type LastDonationData = {
  id: number;
  author: Maybe<Author>;
  donated_amount_in_cents?: number;
  message: Maybe<string>;
};
export type LastDonationProps = StreambotComponentProps<LastDonationWidgetSettings, LastDonationData[]>;
function useData(fundraisingEventId, demoData) {
  const $ = _c(3);
  const locale = useLocale();
  let t0;
  if ($[0] !== locale) {
    t0 = {
      locale
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      selector(param) {
        const mapped = param?.data.map(_temp) ?? [];
        if (!mapped[0]) {
          return null;
        }
        return mapped[0];
      },
      intervalMs: 5000
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}/opinions.json?per_page=1&page=1&facets=has_author_or_amount:true`, t0, t1, demoData);
}
function _temp(opinion) {
  return pick(opinion, ["author", "id", "donated_amount_in_cents", "message"]);
}
function LastDonation(t0) {
  const $ = _c(39);
  const {
    className,
    ...props
  } = t0;
  const merged = getDefaultValues(props);
  const {
    fundraisingEventId,
    demoData: t1,
    withDemoData,
    screenNameFontColor,
    headlineText,
    headlineFont,
    headlineFontColor,
    headlineFontSize,
    headlineFontWeight,
    screenNameFont,
    screenNameFontSize,
    screenNameFontWeight,
    dataFont,
    dataFontColor,
    dataFontSize,
    dataFontWeight,
    commentFont,
    commentFontColor,
    commentFontSize,
    commentFontWeight,
    shouldShowComment,
    countOfLines,
    textAlign,
    ...wrapperProps
  } = merged;
  const demoData = t1 === undefined ? defaultDemoData[0] : t1;
  const fonts = [composeSelectedFontObject(headlineFont, headlineFontWeight), composeSelectedFontObject(dataFont, dataFontWeight), composeSelectedFontObject(commentFont, commentFontWeight), composeSelectedFontObject(screenNameFont, screenNameFontWeight)];
  useGoogleFonts({
    fonts
  });
  const {
    data
  } = useData(fundraisingEventId, withDemoData ? demoData : undefined);
  const alreadyHasDonations = !!data?.id;
  const t = useTranslations("nextjs.streambot");
  const amountCssVariables = getDataCssVariables(dataFont, dataFontSize, dataFontWeight, dataFontColor);
  const commentCssVariables = getCommentCssVariables(commentFont, commentFontSize, commentFontWeight, commentFontColor);
  const clusterAlign = countOfLines === "single";
  const T0 = StreambotWrapper;
  let t2;
  if ($[0] !== className) {
    t2 = classNames("truncate-with-ellipsis", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = getClusterStyles(clusterAlign, textAlign);
  let t4;
  if ($[2] !== headlineFont || $[3] !== headlineFontColor || $[4] !== headlineFontSize || $[5] !== headlineFontWeight || $[6] !== headlineText) {
    t4 = <WidgetHeadline content={headlineText} headlineFont={headlineFont} headlineFontWeight={headlineFontWeight} headlineFontSize={headlineFontSize} headlineFontColor={headlineFontColor} />;
    $[2] = headlineFont;
    $[3] = headlineFontColor;
    $[4] = headlineFontSize;
    $[5] = headlineFontWeight;
    $[6] = headlineText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== alreadyHasDonations || $[9] !== data || $[10] !== t) {
    t5 = alreadyHasDonations ? data.author?.name ?? "" : t("placeholder");
    $[8] = alreadyHasDonations;
    $[9] = data;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== screenNameFont || $[13] !== screenNameFontColor || $[14] !== screenNameFontSize || $[15] !== screenNameFontWeight || $[16] !== t5) {
    t6 = <ScreenName authorName={t5} screenNameFont={screenNameFont} screenNameFontColor={screenNameFontColor} screenNameFontSize={screenNameFontSize} screenNameFontWeight={screenNameFontWeight} />;
    $[12] = screenNameFont;
    $[13] = screenNameFontColor;
    $[14] = screenNameFontSize;
    $[15] = screenNameFontWeight;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== alreadyHasDonations || $[19] !== amountCssVariables || $[20] !== data) {
    t7 = alreadyHasDonations && <Amount amount={data.donated_amount_in_cents} showPlaceholder={true} className="donationData" style={amountCssVariables} />;
    $[18] = alreadyHasDonations;
    $[19] = amountCssVariables;
    $[20] = data;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== alreadyHasDonations || $[23] !== commentCssVariables || $[24] !== data || $[25] !== shouldShowComment) {
    t8 = shouldShowComment && alreadyHasDonations && <Comment comment={data.message} className="truncate-with-ellipsis donationComment" style={commentCssVariables} />;
    $[22] = alreadyHasDonations;
    $[23] = commentCssVariables;
    $[24] = data;
    $[25] = shouldShowComment;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t3 || $[28] !== t4 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
    t9 = <div className={t3}>{t4}{t6}{t7}{t8}</div>;
    $[27] = t3;
    $[28] = t4;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== T0 || $[34] !== t2 || $[35] !== t9 || $[36] !== textAlign || $[37] !== wrapperProps) {
    t10 = <T0 {...wrapperProps} className={t2} textAlign={textAlign}>{t9}</T0>;
    $[33] = T0;
    $[34] = t2;
    $[35] = t9;
    $[36] = textAlign;
    $[37] = wrapperProps;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
}
export default LastDonation;